import * as Sentry from '@sentry/react'
import {
  captureConsoleIntegration,
  dedupeIntegration,
  extraErrorDataIntegration,
  httpClientIntegration,
  reportingObserverIntegration,
} from '@sentry/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

import { classifyApiError, isSilentApiError } from '@affiliate-cabinet/rest'
import { Settings } from 'luxon'

import App from './app'
import { resolveSentryConfiguration } from './shared/config/sentry'
import { initEnvironmentReact } from './shared/initEnvironmentReact'

Settings.defaultNumberingSystem = 'latn'

const { dsn: sentryDsn, enabled: isSentryEnabled } = resolveSentryConfiguration({
  fallbackDsn: initEnvironmentReact.SENTRY_DSN,
  isDevelopment: import.meta.env.DEV,
  runtimeDsn: import.meta.env.VITE_SENTRY_DSN,
})

Sentry.init({
  attachStacktrace: true,
  beforeSend: (event, hint) => {
    const { kind } = classifyApiError(hint.originalException)

    return isSilentApiError(kind) ? null : event
  },
  /**
   * https://docs.sentry.io/platforms/javascript/configuration/filtering/
   */
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_us\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
  dsn: sentryDsn,
  enabled: isSentryEnabled,
  environment: window.location.hostname,
  /**
   * https://docs.sentry.io/platforms/javascript/configuration/filtering/
   */
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
    // reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Other
    'Non-Error exception captured',
    "Cannot get CSS styles from text's parentNode",
    'jsb2',
    'Failed to fetch',
    'Load failed',
    'TypeError: Cancelled',
  ],
  integrations: [
    dedupeIntegration(),
    captureConsoleIntegration({
      levels: ['warn', 'error'],
    }),
    httpClientIntegration(),
    extraErrorDataIntegration({
      depth: 15,
    }),
    reportingObserverIntegration({
      types: ['crash', 'intervention'],
    }),
    Sentry.moduleMetadataIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      createRoutesFromChildren,
      matchRoutes,
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
    }),
    Sentry.replayIntegration({
      networkDetailAllowUrls: [window.location.origin, 'static.cdnaffs.com', 'api.affstore.com'],
      networkRequestHeaders: ['Cache-Control', 'Content-Type'],
      networkResponseHeaders: ['Referrer-Policy'],
    }),
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
  ],
  normalizeDepth: 15,
  // Set profilesSampleRate to 1.0 to profile every transaction.
  // Since profilesSampleRate is relative to tracesSampleRate,
  // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
  // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
  // results in 25% of transactions being profiled (0.5*0.5=0.25)
  profilesSampleRate: 0.2,
  release: import.meta.env.VITE_SENTRY_RELEASE ?? initEnvironmentReact.SENTRY_RELEASE,

  /**
   * https://docs.sentry.io/platforms/javascript/guides/ember/session-replay/#sampling
   * If the entire session is not sampled, use the below sample rate to sample
   * sessions when an error occurs.
   */
  replaysOnErrorSampleRate: 0.2,
  /**
   * https://docs.sentry.io/platforms/javascript/guides/ember/session-replay/#sampling
   * This sets the sample rate to be 10%. You may want this to be 100% while
   * in development and sample at a lower rate in production
   */
  replaysSessionSampleRate: 0.2,
  /**
   * https://docs.sentry.io/platforms/javascript/configuration/options/#sample-rate
   */
  sampleRate: 0.2,
  tracePropagationTargets: [window.location.origin, 'static.cdnaffs.com', 'api.affstore.com'],
  /**
   * https://docs.sentry.io/platforms/javascript/guides/electron/configuration/options/#traces-sample-rate
   * We recommend adjusting this value in production, or using tracesSampler
   * for finer control
   */
  tracesSampleRate: 0.2,
})

const container = document.querySelector('#root')
const root = createRoot(container as Element)

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
